"use client";
import { cn } from "../../lib/utils.mjs";
import * as React from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Slot, Tabs as Tabs$1 } from "radix-ui";
//#region src/components/ui/tabs.tsx
/**
* DESIGN.md amendment 2026-10-18 (CON-1273) — tabs are connected Liquid
* Glass pills. The one look for every tab strip in the product,
* superseding the CON-554 underline (`line`) variant.
*
* - The track: one floating pill — near-opaque glass (`glass/fill menu`),
*   rounded-full, 4px padding, the glass float shadow, no border in light;
*   a 15 % `foreground` hairline under a `.dark` ancestor. It scrolls sideways
*   inside itself when the tabs do not fit.
* - The triggers: plain pills inside the track, no gaps, no separators;
*   `muted-foreground` → `foreground` on hover; the active one
*   `foreground` (flame text on the tint is 2.9:1, under the 4.5:1 floor).
* - The active pill: a flame tint (12 % `surface/select-2` into
*   `background`; a flame rim in dark; `primary` where the marketing
*   tokens are not loaded, i.e. apps/auth) that SLIDES between triggers —
*   position and width over 250 ms ease-out, none under reduced motion.
*   Until the indicator is measured (the server render, the first paint)
*   the active trigger carries the same tint itself, so nothing jumps.
* - An optional count (`count`) on a trigger: a flame badge with jet
*   numerals (white on flame is 3.3:1; jet is 5.2:1).
*
* Two entry points, one look:
* - `Tabs` / `TabsList` / `TabsTrigger` / `TabsContent` — Radix Tabs
*   (`role="tablist"` / `tab`, `aria-selected`, roving focus, ← →). A
*   trigger can be `asChild` around a link; pair it with
*   `activationMode="manual"` so arrow keys move focus and Enter navigates.
* - `PillNav` / `PillNavLink` — routed tabs as a `<nav>` of links with
*   `aria-current="page"` (apps/web `TabNav`); `PillNavLink` is `asChild`
*   around the app's `<Link>`.
*
* Every class below is a literal: Tailwind generates only what it reads.
*/
const Tabs = Tabs$1.Root;
const pillTrackClass = cn("group/pills relative inline-flex max-w-full items-center overflow-x-auto rounded-full p-1", "bg-[color-mix(in_oklab,var(--background)_92%,transparent)] backdrop-blur-glass-nav [scrollbar-width:none]", "border border-foreground/[0.08] [.dark_&]:border-foreground/15", "shadow-[0_10px_30px_-12px_color-mix(in_oklab,var(--shadow-ink)_28%,transparent),0_1px_2px_color-mix(in_oklab,var(--shadow-ink)_8%,transparent)]", "reduce-transparency:bg-background reduce-transparency:backdrop-blur-none");
const pillTriggerClass = cn("relative z-[1] inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-1.5", "text-sm font-medium text-muted-foreground transition-[color] duration-200 hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background", "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50", "data-[state=active]:text-foreground aria-[current=page]:text-foreground", "group-data-[indicator=pending]/pills:data-[state=active]:bg-[color-mix(in_oklab,var(--color-surface-select-2,var(--primary))_12%,var(--background))]", "group-data-[indicator=pending]/pills:aria-[current=page]:bg-[color-mix(in_oklab,var(--color-surface-select-2,var(--primary))_12%,var(--background))]");
const indicatorClass = cn("pointer-events-none absolute inset-y-1 left-0 rounded-full", "bg-[color-mix(in_oklab,var(--color-surface-select-2,var(--primary))_12%,var(--background))]", "[.dark_&]:border [.dark_&]:border-[color:color-mix(in_oklab,var(--color-surface-select-2,var(--primary))_60%,transparent)]", "transition-[transform,width] duration-[250ms] ease-out motion-reduce:transition-none");
const useIsomorphicLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;
const ACTIVE_SELECTOR = "[role=\"tab\"][data-state=\"active\"], [aria-current=\"page\"]";
/** Where the active trigger sits inside the track, re-measured whenever selection or size changes. */
function useActivePill(trackRef) {
	const [box, setBox] = React.useState(null);
	useIsomorphicLayoutEffect(() => {
		const track = trackRef.current;
		if (!track) return;
		const measure = () => {
			const active = track.querySelector(ACTIVE_SELECTOR);
			if (!active) {
				setBox(null);
				return;
			}
			const a = active.getBoundingClientRect();
			const t = track.getBoundingClientRect();
			const next = {
				x: a.left - t.left - track.clientLeft + track.scrollLeft,
				width: a.width
			};
			const view = track.clientWidth;
			if (view > 0 && (next.x < track.scrollLeft || next.x + next.width > track.scrollLeft + view)) track.scrollLeft = Math.max(0, next.x - 16);
			setBox((prev) => prev && prev.x === next.x && prev.width === next.width ? prev : next);
		};
		measure();
		const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
		resize?.observe(track);
		for (const child of Array.from(track.children)) resize?.observe(child);
		const mutations = new MutationObserver(measure);
		mutations.observe(track, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ["data-state", "aria-current"]
		});
		return () => {
			resize?.disconnect();
			mutations.disconnect();
		};
	}, [trackRef]);
	return box;
}
/** The sliding flame pill behind the active trigger. */
function ActivePill({ box, as: Tag = "span" }) {
	if (!box) return null;
	return /* @__PURE__ */ jsx(Tag, {
		"aria-hidden": true,
		role: Tag === "li" ? "presentation" : void 0,
		"data-tabs-indicator": "",
		className: indicatorClass,
		style: {
			width: box.width,
			transform: `translateX(${box.x}px)`
		}
	});
}
const TabsList = React.forwardRef(function TabsList({ className, children, ...props }, ref) {
	const trackRef = React.useRef(null);
	const setRef = React.useCallback((node) => {
		trackRef.current = node;
		if (typeof ref === "function") ref(node);
		else if (ref) ref.current = node;
	}, [ref]);
	const box = useActivePill(trackRef);
	return /* @__PURE__ */ jsxs(Tabs$1.List, {
		ref: setRef,
		"data-indicator": box ? "ready" : "pending",
		className: cn(pillTrackClass, className),
		...props,
		children: [/* @__PURE__ */ jsx(ActivePill, { box }), children]
	});
});
/** A count on a trigger: a flame badge with jet numerals (5.2:1). */
function Count({ value }) {
	return /* @__PURE__ */ jsx("span", {
		"data-tabs-count": "",
		className: "inline-flex min-w-5 items-center justify-center rounded-full bg-surface-select-2 px-1.5 text-xs font-semibold tabular-nums text-text-heading-1",
		children: value
	});
}
const TabsTrigger = React.forwardRef(function TabsTrigger({ className, count, children, asChild, ...props }, ref) {
	return /* @__PURE__ */ jsx(Tabs$1.Trigger, {
		ref,
		asChild,
		className: cn(pillTriggerClass, className),
		...props,
		children: asChild || count === void 0 ? children : /* @__PURE__ */ jsxs(Fragment, { children: [children, /* @__PURE__ */ jsx(Count, { value: count })] })
	});
});
const TabsContent = React.forwardRef(function TabsContent({ className, ...props }, ref) {
	return /* @__PURE__ */ jsx(Tabs$1.Content, {
		ref,
		className: cn("mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className),
		...props
	});
});
/** Routed tabs: a `<nav>` holding the pill track; each item is a `PillNavLink`. */
function PillNav({ label, className, children, ...props }) {
	const trackRef = React.useRef(null);
	const box = useActivePill(trackRef);
	return /* @__PURE__ */ jsx("nav", {
		"aria-label": label,
		className: cn("max-w-full", className),
		...props,
		children: /* @__PURE__ */ jsxs("ul", {
			ref: trackRef,
			"data-indicator": box ? "ready" : "pending",
			className: pillTrackClass,
			children: [/* @__PURE__ */ jsx(ActivePill, {
				box,
				as: "li"
			}), children]
		})
	});
}
function PillNavLink({ active, count, className, children }) {
	return /* @__PURE__ */ jsx("li", {
		className: "flex",
		children: /* @__PURE__ */ jsx(Slot.Root, {
			"aria-current": active ? "page" : void 0,
			className: cn(pillTriggerClass, className),
			children: count === void 0 ? children : React.cloneElement(children, void 0, /* @__PURE__ */ jsxs(Fragment, { children: [children.props.children, /* @__PURE__ */ jsx(Count, { value: count })] }))
		})
	});
}
//#endregion
export { PillNav, PillNavLink, Tabs, TabsContent, TabsList, TabsTrigger, pillTrackClass, pillTriggerClass, useActivePill };
